import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Form,
  DatePicker,
  Button,
  Typography,
  Row,
  Col,
  Descriptions,
  Select,
  Popconfirm,
  message,
  Table,
} from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import {
  QUERY_ASSIGNMENTS,
  QUERY_ONE_ASSIGNMENT,
  MUTATION_UPDATE_ASSIGNMENT,
  MUTATION_REMOVE_ASSIGNMENT,
  QUERY_LIST_ASSIGNMENT_EXTENSION_HISTORY,
} from 'queries/AssignmentsQueries';
import { QUERY_ASSIGNMENT_FEE_LEDGER } from 'queries/FeeLedgerQueries';
import { QUERY_LIST_LOCATIONS } from '../Locations/queries';
import { CONTAINER_CHECK_IN } from '../../../queries/ContainersQueries';
import dayjs from 'dayjs';
import routes from '../../../constants/routes';
import { AssignmentStatuses } from '../../../constants/AssignmentStatuses';
import numeral from 'numeral';
import { CURRENCY_FORMAT } from 'constants/numberformats';
import { Link, useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import FeeLedgerTable from '../../../components/Tables/FeeLedgerTable';
import { UserContext } from '../../../contexts/UserContext';
import PermissionsAlert from '../../../components/PermissionsAlert/PermissionsAlert';

import './Assignment.sass';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(localizedFormat);

const { Title } = Typography;
const USEFULL_CUSTOMER_SERVICE_LOCATION_ID = 4;

const AssignmentEdit = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { dbUser } = useContext(UserContext);
  const [saving, setSaving] = useState(false);
  const [toLocation, setToLocation] = useState<number | null>(null);
  const [messageApi, contextHolder] = message.useMessage();
  const {
    data: assignmentData,
    error: assignmentError,
    loading: assignmentLoading,
    refetch: refetchAssignment,
  } = useQuery(QUERY_ONE_ASSIGNMENT, {
    variables: {
      assignment_id: params.assignment_id,
    },
    fetchPolicy: 'no-cache',
  });

  const assignment = useMemo(
    () => assignmentData?.getAssignment || {},
    [assignmentData],
  );

  const { data: feeLedgerData, error: feeLedgerError } = useQuery(
    QUERY_ASSIGNMENT_FEE_LEDGER,
    {
      variables: {
        assignment_id: params.assignment_id,
      },
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
    },
  );

  const { data: listLocationsData } = useQuery(QUERY_LIST_LOCATIONS, {
    variables: {
      where: dbUser?.isCorporateAdmin
        ? {
            corporate_client_id: {
              in: dbUser?.userCorporateClients?.map(
                (uc) => uc.corporate_client_id,
              ),
            },
          }
        : {},
      order: [
        { field: 'pretty_name', order: 'ASC' },
        { field: 'name', order: 'ASC' },
      ],
      pagination: {
        page: 1,
        size: 200,
      },
    },
  });

  const listLocations = useMemo(
    () => listLocationsData?.listLocation,
    [listLocationsData],
  );

  const [updateAssignment] = useMutation(MUTATION_UPDATE_ASSIGNMENT, {
    refetchQueries: [QUERY_ASSIGNMENTS, QUERY_ONE_ASSIGNMENT],
  });

  const [removeAssignment] = useMutation(MUTATION_REMOVE_ASSIGNMENT, {
    refetchQueries: [QUERY_ASSIGNMENTS],
  });

  const [containerCheckIn, { loading: containerCheckInLoading }] =
    useMutation(CONTAINER_CHECK_IN);

  const { data: extensionHistoryData, loading: extensionHistoryLoading } =
    useQuery(QUERY_LIST_ASSIGNMENT_EXTENSION_HISTORY, {
      variables: {
        where: {
          assignment_id: { eq: params.assignment_id },
        },
        order: [{ field: 'extension.started_on', order: 'DESC' }],
      },
    });

  const disableDueOnField = useMemo(
    () =>
      assignment ? assignment?.status_id !== AssignmentStatuses.Active : true,
    [assignment],
  );

  const handleEditSubmit = useCallback(
    async (values: any) => {
      setSaving(true);

      await updateAssignment({
        variables: {
          assignment_id: params.assignment_id,
          input: {
            ...values,
            from_location_id: assignment?.from_location_id,
            container_id: assignment?.container_id,
            user_id: assignment?.user_id,
            status_id: assignment?.status_id,
          },
        },
      });
      setSaving(false);
    },
    [assignment, updateAssignment, params],
  );

  useEffect(() => {
    const [firstLocation] = listLocations?.list || [];
    if (firstLocation) {
      setToLocation(
        process.env.REACT_APP_ENV === 'production'
          ? USEFULL_CUSTOMER_SERVICE_LOCATION_ID
          : firstLocation.location_id,
      );
    }
  }, [listLocations]);

  const handleUpdateStatus = useCallback(
    async (status_id: AssignmentStatuses) => {
      setSaving(true);
      await updateAssignment({
        variables: {
          assignment_id: params.assignment_id,
          input: {
            status_id,
          },
        },
      });
      setSaving(false);
    },
    [params.assignment_id, updateAssignment],
  );

  const handleChangeLocation = useCallback(
    (location: any) => {
      setToLocation(location);
    },
    [setToLocation],
  );

  const handleReturn = useCallback(async () => {
    try {
      await containerCheckIn({
        variables: {
          container_id: assignment?.container?.container_id,
          to_location_id: toLocation,
          returned_by: dbUser?.user_id,
        },
      });
      await refetchAssignment();
    } catch (e) {
      console.error(e);
    }
  }, [toLocation, assignment]);

  if (assignmentLoading) {
    return <Title level={3}>loading</Title>;
  }

  if (assignmentError) {
    return (
      <Title level={3}>
        ERROR: <b>{assignmentError?.message}</b>
      </Title>
    );
  }

  const chargedLateFees = _.sumBy(
    assignment.feeLedgerRecords.filter(
      (r: Record<string, any>) => r.processed_on,
    ),
    (r: Record<string, any>) => r.amount,
  );

  const assignmentHasTransactions = !!assignment.feeLedgerRecords.find(
    (r: Record<string, any>) => r?.fee_transactions?.length ?? 0,
  );

  const userMembership =
    assignment?.user?.user_membership[
      assignment?.user?.user_membership.length - 1
    ];
  const paymentProcessorName = userMembership?.paymentProcessor.name;

  if (!dbUser?.userPermissions.ManageAssignments) {
    return <PermissionsAlert />;
  }

  return (
    <Row gutter={10} className="assignment">
      <Col span={24}>
        <Descriptions
          column={1}
          bordered
          title={
            assignment.user ? (
              dbUser?.userPermissions.ManageUsers ? (
                <Link to={routes.USERS + '/' + assignment.user_id}>
                  {assignment?.user?.full_name}
                </Link>
              ) : (
                assignment?.user?.full_name
              )
            ) : (
              <Title>unknown</Title>
            )
          }
        >
          {assignment?.user?.email && (
            <Descriptions.Item label="Email">
              <Button
                onClick={(e) => {
                  window.location.href = `mailto:${assignment.user.email}`;
                  e.preventDefault();
                }}
              >
                {assignment.user.email}
              </Button>
            </Descriptions.Item>
          )}
          {assignment.returned_by_user?.email && (
            <Descriptions.Item label="Returned by">
              {assignment.returned_by_user.email}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Container">
            {dbUser?.userPermissions.ManageCups ||
            dbUser?.userPermissions.ViewCups ? (
              <Link
                to={
                  routes.CONTAINERS + '/' + assignment?.container?.container_id
                }
              >
                {assignment?.container?.unique_name || 'unknown'}
              </Link>
            ) : (
              assignment?.container?.unique_name || 'unknown'
            )}
          </Descriptions.Item>
          {assignment?.container?.corporateClient && (
            <Descriptions.Item label="Corp client">
              {dbUser?.userPermissions.ManageCorporateClients ? (
                <Link
                  to={
                    routes.CORPORATE_CLIENTS +
                    '/' +
                    assignment?.container?.corporateClient?.corporate_client_id
                  }
                >
                  {assignment?.container?.corporateClient?.name}
                </Link>
              ) : (
                assignment?.container?.corporateClient?.name
              )}
            </Descriptions.Item>
          )}
          {assignment.assigned_on && (
            <Descriptions.Item label="Start date">
              {dayjs(assignment.assigned_on).format('LLL')}
            </Descriptions.Item>
          )}
          {assignment.returned_on && (
            <Descriptions.Item label="End date">
              {dayjs(assignment.returned_on).format('LLL')}
            </Descriptions.Item>
          )}
          {assignment.from_location && (
            <Descriptions.Item label="From Location">
              {assignment.from_location?.pretty_name ??
                assignment.from_location?.name}
            </Descriptions.Item>
          )}
          {assignment.to_location && (
            <Descriptions.Item label="To Location">
              {assignment.to_location?.pretty_name ??
                assignment.to_location?.name}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Charged late fees">
            <Row gutter={10} align="middle">
              <Col>{numeral(chargedLateFees).format(CURRENCY_FORMAT)}</Col>
            </Row>
          </Descriptions.Item>
          <Descriptions.Item label={'Fee ledger'}>
            {feeLedgerData && !feeLedgerError && (
              <FeeLedgerTable
                data={feeLedgerData?.getFeeLedger}
                showActionButtons
              />
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Refunded amount">
            {numeral(assignment.assignment_refunded_amount).format(
              CURRENCY_FORMAT,
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Uncharged Late Fees">
            {numeral(
              _.sumBy(
                assignment.feeLedgerRecords.filter(
                  (r: Record<string, any>) =>
                    !r.processed_on && r.amount > 0 && !r.is_voided,
                ),
                (r: Record<string, any>) => r.amount,
              ),
            ).format(CURRENCY_FORMAT)}
          </Descriptions.Item>
          {assignment?.user?.current_client_level?.membershipLevel?.name && (
            <Descriptions.Item label="User membership level">
              {assignment.user.current_client_level.membershipLevel.name}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Status">
            {assignment?.status?.name?.[0]?.toUpperCase()}
            {assignment?.status?.name?.slice(1)}
            {[AssignmentStatuses.Active, AssignmentStatuses.Paused].includes(
              assignment?.status_id,
            ) && (
              <>
                <Button
                  loading={saving}
                  type="primary"
                  htmlType="submit"
                  style={{ marginLeft: 10 }}
                  onClick={() =>
                    handleUpdateStatus(
                      assignment.status_id === AssignmentStatuses.Paused
                        ? AssignmentStatuses.Active
                        : AssignmentStatuses.Paused,
                    )
                  }
                >
                  {assignment.status_id === AssignmentStatuses.Paused
                    ? 'Activate'
                    : 'Pause'}
                </Button>
                <div className="assignment-space space-lg" />
                {[
                  AssignmentStatuses.Active,
                  AssignmentStatuses.Paused,
                ].includes(assignment?.status_id) &&
                  !assignment.returned_on && (
                    <>
                      <Select
                        value={toLocation}
                        className="assignment-select"
                        options={listLocations?.list?.map((option: any) => {
                          const name = option?.pretty_name || option?.name;
                          return {
                            label: name,
                            value: option?.location_id,
                          };
                        })}
                        onChange={handleChangeLocation}
                      />
                      <div className="assignment-space space-xs" />
                      <Button
                        loading={containerCheckInLoading}
                        type="primary"
                        htmlType="submit"
                        style={{ marginLeft: 10 }}
                        onClick={handleReturn}
                        disabled={!toLocation}
                      >
                        Return
                      </Button>
                    </>
                  )}
              </>
            )}
            {!chargedLateFees &&
              dbUser?.userPermissions.DangerZone &&
              !assignmentHasTransactions && (
                <>
                  <div className="assignment-space space-lg" />
                  {contextHolder}
                  <Popconfirm
                    title="Are you sure you want to delete this assignment entirely?"
                    onConfirm={async () => {
                      const removeRes = await removeAssignment({
                        variables: {
                          assignment_id: assignment.assignment_id,
                        },
                      });

                      if (
                        removeRes.data.removeAssignmentWithoutFees.error_message
                      ) {
                        messageApi.error(
                          removeRes.data.removeAssignmentWithoutFees
                            .error_message.message,
                        );
                      } else {
                        navigate(routes.ASSIGNMENTS);
                      }
                    }}
                  >
                    <Button danger>Delete</Button>
                  </Popconfirm>
                </>
              )}
          </Descriptions.Item>
          {assignment.note && (
            <Descriptions.Item label="Note">
              {assignment.note}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Payment processor">
            {paymentProcessorName || 'unknown'}
          </Descriptions.Item>
          <Descriptions.Item label="Due On">
            <Form
              layout="inline"
              name="assignment-edit"
              onFinish={handleEditSubmit}
              initialValues={{
                ...assignment,
                due_on: dayjs(assignment?.due_on),
              }}
            >
              <Form.Item
                label="Due on"
                name="due_on"
                rules={[{ required: true, message: 'Please input Due On!' }]}
              >
                <DatePicker disabled={disableDueOnField} placeholder="Due On" />
              </Form.Item>

              {!disableDueOnField && (
                <Button loading={saving} type="primary" htmlType="submit">
                  Save
                </Button>
              )}
            </Form>
            {disableDueOnField
              ? 'you are not able to edit the due date of a completed assignment'
              : ''}
          </Descriptions.Item>

          {assignment?.lastExtension && (
            <Descriptions.Item label="Extension History">
              <Table
                rowKey="extension_id"
                columns={[
                  {
                    title: 'Reason',
                    dataIndex: ['extension', 'internal_note'],
                    key: 'reason',
                    width: 300,
                  },
                  {
                    title: 'Days Extended',
                    dataIndex: ['extension', 'days'],
                    key: 'days',
                    width: 120,
                  },
                  {
                    title: 'Fees Voided',
                    dataIndex: 'fees_voided',
                    key: 'fees_voided',
                    width: 120,
                    render: (value: number) =>
                      numeral(value).format(CURRENCY_FORMAT),
                  },
                  {
                    title: 'Fees Refunded',
                    dataIndex: 'fees_refunded',
                    key: 'fees_refunded',
                    width: 120,
                    render: (value: number) =>
                      numeral(value).format(CURRENCY_FORMAT),
                  },
                ]}
                dataSource={
                  extensionHistoryData?.listAssignmentExtensionHistory?.list ||
                  []
                }
                loading={extensionHistoryLoading}
                pagination={{
                  pageSize: 5,
                  showTotal: (total, range) =>
                    `${range[0]} - ${range[1]} of ${total} extensions`,
                }}
                size="small"
              />
            </Descriptions.Item>
          )}
        </Descriptions>
      </Col>
    </Row>
  );
};

export default AssignmentEdit;
